"use client";

import { classed } from "@trydig/ui";
import type { ReactNode } from "react";
import { startTransition, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { ProductCard, ProductCardSkeleton } from "@/components/ui/ProductCard";
import { Typography } from "@/components/ui/Typography";
import { FilterModal } from "@/features/products/ProductListingPage/components/FilterModal";
import { PRODUCTS_PER_PAGE } from "@/features/products/ProductListingPage/config";
import { usePLP } from "@/features/products/ProductListingPage/hooks/PLPContext";
import { useSetProductListState } from "@/features/products/ProductListingPage/hooks/useProductListState";
import { useTranslations } from "@/i18n/client";

const MOBILE_COLUMNS = 2; // grid-cols-2
const TABLET_COLUMNS = 2; // grid-cols-2 (tablet matches mobile now)
const DESKTOP_COLUMNS = 4; // lg:grid-cols-4

export interface ProductListProps {
	content?: Array<{
		element: ReactNode;
		mobileRowStart: number;
		mobileRows: number;
		desktopRowStart: number;
	}>;
}

export const ProductList = ({ content }: ProductListProps) => {
	const t = useTranslations();
	const setProductListState = useSetProductListState();
	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isFetching,
		isLoading,
		isFiltered,
		clear,
	} = usePLP();

	// Dedupe by product id. Depict can merchandise two variants of the same
	// product as separate displays, and cursor pages can overlap on retries —
	// either path produces duplicate React keys in the grid. Keep the first
	// occurrence so Depict's merchandising order wins.
	const seenIds = new Set<string>();
	const allProducts = (data?.pages.flatMap((page) => page.products) ?? []).filter((p) => {
		const id = String(p.id);
		if (seenIds.has(id)) return false;
		seenIds.add(id);
		return true;
	});
	// Total comes from the first page (Depict n_hits or Centra productCount);
	// later pages can be terminators with total: 0 if Depict fails mid-session.
	const totalProducts = data?.pages[0]?.total ?? 0;
	const remainingProducts = totalProducts - allProducts.length;

	useEffect(() => {
		setProductListState({
			loaded: allProducts.length,
			total: totalProducts,
		});
	}, [allProducts.length, totalProducts, setProductListState]);

	const visibleContentBloks =
		content
			?.filter((contentItem, i) => {
				const { mobileRowStart, desktopRowStart } = contentItem;

				// Mobile calculation (< 640px)
				const requiredForMobile = (mobileRowStart - 1) * MOBILE_COLUMNS;
				let adjustedRequiredMobile = requiredForMobile;

				// Tablet calculation (640px - 1024px, sm breakpoint)
				const requiredForTablet = (desktopRowStart - 1) * TABLET_COLUMNS;
				let adjustedRequiredTablet = requiredForTablet;

				// Desktop calculation (>= 1024px, lg breakpoint)
				const requiredForDesktop = (desktopRowStart - 1) * DESKTOP_COLUMNS;
				let adjustedRequiredDesktop = requiredForDesktop;

				for (let j = 0; j < i; j++) {
					if (!content[j]) continue;

					const prevMobileRowStart = content[j].mobileRowStart;
					const prevMobileRows = content[j].mobileRows;
					const prevDesktopRowStart = content[j].desktopRowStart;

					if (prevMobileRowStart < mobileRowStart) {
						// Subtract the total slots occupied by previous content (rows * columns)
						adjustedRequiredMobile -= prevMobileRows * 2; // 2 columns on mobile
					}
					if (prevDesktopRowStart < desktopRowStart) {
						adjustedRequiredTablet -= 2; // Content spans 2 columns on tablet
						adjustedRequiredDesktop -= 2; // Content spans 2 columns on desktop
					}
				}

				// Use the minimum required products across all breakpoints
				// This ensures content shows when it fits in any layout
				const requiredProducts = Math.min(
					adjustedRequiredMobile,
					adjustedRequiredTablet,
					adjustedRequiredDesktop,
				);

				return allProducts.length >= requiredProducts;
			})
			.map((contentItem) => contentItem.element) ?? [];

	const isBusy = isFetchingNextPage || isFetching || isLoading;
	const showEmptyState = !isBusy && allProducts.length === 0;
	const firstPageError = data?.pages[0]?.error ?? null;

	return (
		<>
			<FilterModal />
			{showEmptyState ? (
				<EmptyState
					isFiltered={isFiltered}
					error={firstPageError}
					onClearFilters={() => startTransition(() => clear())}
				/>
			) : (
				<Grid className="lg:grid-cols-4 lg:[--grid-cols:4]">
					{visibleContentBloks}
					{allProducts.map((product, i) => (
						<ProductCard
							key={product.id}
							product={product}
							size="large"
							showBrand={false}
							// Flag the first-row cards as priority — they sit in the LCP
							// region. Max grid is 4 cols, so the first 4 always cover it.
							priority={i < 4}
						/>
					))}
					{isBusy &&
						Array.from({ length: PRODUCTS_PER_PAGE }).map((_, i) => (
							<ProductCardSkeleton key={i} />
						))}
				</Grid>
			)}
			{hasNextPage && (
				<div className="px-grid-section-vertical-lg flex justify-center">
					<Button
						size="large"
						onClick={() => fetchNextPage()}
						className="mb-grid-section-vertical-lg w-full lg:w-auto"
						toolTip={remainingProducts}
						loading={isFetchingNextPage}
					>
						{t("products.loadMore")}
					</Button>
				</div>
			)}
		</>
	);
};

const EmptyState = ({
	isFiltered,
	error,
	onClearFilters,
}: {
	isFiltered: boolean;
	error: string | null;
	onClearFilters: () => void;
}) => {
	const t = useTranslations();
	const isError = Boolean(error) && error !== "missing_category";

	const heading = isFiltered
		? t("No products match your filters")
		: isError
			? t("We couldn't load products right now")
			: t("No products available");

	const body = isFiltered
		? t("Try removing a filter or two to see more results.")
		: isError
			? t("Please refresh the page or try again in a moment.")
			: t("Check back soon — new arrivals land regularly.");

	return (
		<div className="w-full py-grid-section-vertical-xl min-h-[40vh]">
			<div className="mx-auto w-full max-w-2xl text-center space-y-grid-section-vertical-md">
				<Typography variant="heading-md" as="h2" className="block">
					{heading}
				</Typography>
				<Typography variant="body-sm-reg" as="p" className="block text-text-dark-text-2">
					{body}
				</Typography>
				{isFiltered && (
					<div className="flex justify-center pt-grid-section-vertical-sm">
						<Button variant="outline" color="secondary" size="large" onClick={onClearFilters}>
							{t("Clear all filters")}
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};

const Grid = classed.section("grid grid-cols-2 gap-(--layout-gap) auto-rows-auto [--grid-cols:2]");

export const ProductListSkeleton = ({ size = PRODUCTS_PER_PAGE }: { size?: number }) => {
	return (
		<Grid>
			{Array.from({ length: size }).map((_, i) => (
				<ProductCardSkeleton key={i} />
			))}
		</Grid>
	);
};
